(() => {
  if (window.BaikallaLive) return;
  const ID = /^[A-Za-z0-9_-]{11}$/;
  const listeners = new Map();
  const unknown = () => ({ live: false, known: false });
  function subscribe(endpoint, callback) {
    if (!endpoint) { callback(unknown()); return () => {}; }
    let url;
    try { url = new URL(endpoint, location.origin); if (url.protocol !== 'https:') throw Error(); }
    catch { callback(unknown()); return () => {}; }
    const key = url.href;
    let entry = listeners.get(key);
    if (!entry) {
      entry = { callbacks: new Set(), value: unknown(), timer: null, stopped: false };
      listeners.set(key, entry);
      const poll = async () => {
        if (entry.stopped) return;
        if (!document.hidden) {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 6000);
          try {
            const response = await fetch(key, { signal: controller.signal, credentials: 'omit', cache: 'no-store', referrerPolicy: 'strict-origin-when-cross-origin' });
            if (!response.ok) throw Error();
            const data = await response.json();
            const age = Date.now() - Date.parse(data.updatedAt);
            if (!Number.isFinite(age) || age < -30000 || age > 180000 || data.known !== true || typeof data.live !== 'boolean') throw Error();
            if (data.live && !ID.test(data.videoId || '')) throw Error();
            entry.value = data;
          } catch { entry.value = unknown(); }
          finally { clearTimeout(timeout); }
          entry.callbacks.forEach(fn => fn(entry.value));
        }
        if (!entry.stopped) entry.timer = setTimeout(poll, 60000);
      };
      entry.poll = poll;
      entry.onVisibility = () => {
        if (!document.hidden && !entry.stopped) { clearTimeout(entry.timer); entry.poll(); }
      };
      document.addEventListener('visibilitychange', entry.onVisibility);
      poll();
    }
    entry.callbacks.add(callback);
    callback(entry.value);
    return () => {
      entry.callbacks.delete(callback);
      if (!entry.callbacks.size) {
        entry.stopped = true;
        clearTimeout(entry.timer);
        document.removeEventListener('visibilitychange', entry.onVisibility);
        listeners.delete(key);
      }
    };
  }
  window.BaikallaLive = { subscribe };
  class Banner extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      if (this.dataset.mode === 'on') { this.hidden = false; return; }
      this.hidden = true;
      if (this.dataset.mode !== 'auto') return;
      this.cleanup = subscribe(this.dataset.endpoint, data => { this.hidden = !(data.known && data.live); });
    }
    disconnectedCallback() { this.cleanup?.(); }
  }
  class ShopLive extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      this.player = this.querySelector('[data-player]');
      this.button = this.querySelector('[data-play]');
      this.button.onclick = () => this.play();
      this.chatRegion = this.querySelector('[data-chat-region]');
      this.chatFrame = this.querySelector('[data-chat-frame]');
      this.chatPrompt = this.querySelector('[data-chat-prompt]');
      this.chatMedia = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
      if (this.onChatMedia) this.chatMedia.removeEventListener('change', this.onChatMedia);
      this.onChatMedia = () => this.updateChat();
      this.chatMedia.addEventListener('change', this.onChatMedia);
      this.cleanup = subscribe(this.dataset.endpoint, data => this.update(data));
    }
    update(data) {
      const live = data.known === true && data.live === true && ID.test(data.videoId || '');
      this.isLive = live;
      this.dataset.live = String(live);
      const id = live ? data.videoId : this.dataset.featured;
      const next = ID.test(id || '') ? id : '';
      if (next !== this.videoId) {
        this.player.querySelector('iframe')?.remove();
        this.startedVideoId = '';
        this.querySelector('[data-cover]').hidden = false;
      }
      this.videoId = next;
      this.button.hidden = !next;
      this.button.textContent = live ? 'Watch live' : 'Watch featured broadcast';
      this.querySelector('[data-status]').textContent = live ? '● Live now — join us' : data.known ? 'We’re currently offline. Explore a featured broadcast below.' : 'Live status is unavailable. Visit YouTube for the latest updates.';
      const youtubeLink = this.querySelector('[data-youtube]');
      youtubeLink.href = next ? `https://www.youtube.com/watch?v=${next}` : 'https://www.youtube.com/@BaikallaJewelry/live';
      youtubeLink.textContent = live ? 'Join the chat on YouTube ↗' : 'Open this broadcast on YouTube ↗';
      this.updateChat();
    }
    play() {
      if (!ID.test(this.videoId || '') || this.player.querySelector('iframe')) return;
      const iframe = document.createElement('iframe');
      iframe.title = 'Baikalla Jewelry YouTube broadcast';
      iframe.src = `https://www.youtube-nocookie.com/embed/${this.videoId}?playsinline=1&autoplay=1`;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      this.querySelector('[data-cover]').hidden = true;
      this.player.append(iframe);
      this.startedVideoId = this.videoId;
      this.updateChat();
    }
    updateChat() {
      if (!this.chatRegion || !this.chatFrame) return;
      const desktop = this.chatMedia.matches;
      this.dataset.chatDesktop = String(desktop);
      const eligible = this.isLive && desktop && ID.test(this.videoId || '');
      this.chatRegion.hidden = !eligible;
      const existing = this.chatFrame.querySelector('iframe');
      if (!eligible || this.startedVideoId !== this.videoId) {
        existing?.remove();
        if (this.chatPrompt) this.chatPrompt.hidden = false;
        return;
      }
      const url = new URL('https://www.youtube.com/live_chat');
      url.searchParams.set('v', this.videoId);
      url.searchParams.set('embed_domain', location.hostname);
      if (existing?.src === url.href) return;
      existing?.remove();
      const iframe = document.createElement('iframe');
      iframe.title = 'Baikalla Jewelry YouTube live chat';
      iframe.src = url.href;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      this.chatFrame.append(iframe);
      if (this.chatPrompt) this.chatPrompt.hidden = true;
    }
    disconnectedCallback() {
      this.cleanup?.();
      if (this.onChatMedia) this.chatMedia?.removeEventListener('change', this.onChatMedia);
      this.chatFrame?.querySelector('iframe')?.remove();
    }
  }
  if (!customElements.get('baikalla-live-banner')) customElements.define('baikalla-live-banner', Banner);
  if (!customElements.get('baikalla-shop-live')) customElements.define('baikalla-shop-live', ShopLive);
})();
